import MuiLinearProgress from '@mui/material/LinearProgress';
import type { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import {
    createStyles,
    withStyles,
} from '@mui/styles';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

const commonStyles = (theme: Theme) => ({
    root: {
        height: 4,
        borderRadius: 4,
        marginTop: '10px',
    },
    colorPrimary: { backgroundColor: theme.palette.semantic.colorBackgroundDisabled },
    bar: {
        borderRadius: 4,
        backgroundColor: theme.palette.semantic.colorPrimary,
    },
});
const RegularLinearProgress = withStyles(theme => createStyles(commonStyles(theme)))(MuiLinearProgress);

const OverAllocatedLinearProgress = withStyles(theme =>
    createStyles({
        ...commonStyles(theme),
        colorPrimary: { backgroundColor: theme.palette.semantic.colorErrorIcon },
        bar: { backgroundColor: theme.palette.semantic.colorErrorIcon },
    }),
)(MuiLinearProgress);

export const BorderLinearProgress: React.FC<{
    consumed: number;
    total: number;
    unlimited?: boolean;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
}> = ({
    consumed, total, unlimited = false, ariaLabelledBy, ariaDescribedBy,
}) => {
    const {
        formatMessage: translate, formatNumber,
    } = useIntl();

    const tooltipText = useMemo(() => (unlimited
        ? translate(
            { id: 'CLIENT_ALLOCATED_IN_USE' },
            { 0: formatNumber(consumed) },
        )
        : translate(
            { id: 'CLIENT_ALLOCATED_OF_TOTAL' },
            {
                0: formatNumber(consumed),
                1: formatNumber(total),
            },
        )), [ translate, formatNumber, consumed, total, unlimited ]);

    // Unlimited products have no meaningful total: empty track without usage,
    // full blue bar with usage, and never the over-allocated (red) state.
    const percentage = useMemo(() => {
        if (unlimited) {
            return consumed > 0 ? 100 : 0;
        }
        if (consumed > total) {
            return 101;
        }
        // Guard against 0/0 producing NaN, which MUI LinearProgress can't render
        return total > 0 ? Math.round((consumed / total) * 100) : 0;
    }, [ consumed, total, unlimited ]);

    return (
        <Tooltip
            title={tooltipText}
            data-testid="tooltip"
            aria-label={tooltipText}
            aria-describedby={ariaDescribedBy}>
            {percentage > 100 ? (
                <OverAllocatedLinearProgress
                    data-testid="over-allocated"
                    variant="determinate"
                    value={percentage}
                    aria-labelledby={ariaLabelledBy}
                    aria-valuetext={tooltipText}
                />
            ) : (
                <RegularLinearProgress
                    data-testid="regular"
                    variant="determinate"
                    value={percentage}
                    aria-labelledby={ariaLabelledBy}
                    aria-valuetext={tooltipText}
                />
            )}
        </Tooltip>
    );
};
